import { useState, useCallback, useEffect } from "react";
import { signIn, completeNewPassword, getCurrentSessionToken, signOutCurrentUser } from "../services/cognito";
import type { CognitoUser } from "amazon-cognito-identity-js";
import { fetchUserProfile } from "../services/userService";
import type { UserProfile } from "../services/userService";

type AuthStep = "LOGIN" | "NEW_PASSWORD" | "AUTHENTICATED";

interface UseLoginReturn {
  username: string;
  password: string;
  newPassword: string;
  confirmPassword: string;
  error: string;
  isLoading: boolean;
  step: AuthStep;
  userProfile: UserProfile | null;
  setUsername: (value: string) => void;
  setPassword: (value: string) => void;
  setNewPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  handleLogin: () => Promise<void>;
  handleNewPassword: () => Promise<void>;
  handleLogout: () => void;
  refreshProfile: () => Promise<void>;
  isInitializing: boolean;
}

export function useLogin(): UseLoginReturn {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [step, setStep] = useState<AuthStep>("LOGIN");
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  // Store cognito user & attributes for the new-password challenge
  const [pendingUser, setPendingUser] = useState<CognitoUser | null>(null);
  const [pendingAttributes, setPendingAttributes] = useState<Record<string, string>>({});

  // Auto-login active session
  useEffect(() => {
    const checkActiveSession = async () => {
      try {
        const token = await getCurrentSessionToken();
        if (token) {
          const profile = await fetchUserProfile(token);
          if (profile.roles.includes("ADMIN")) {
            setUserProfile(profile);
            setStep("AUTHENTICATED");
          } else {
            setError("Acceso denegado. No tenés permisos de administrador.");
            setStep("LOGIN");
          }
        }
      } catch (e) {
        console.error("No active session or session expired:", e);
      } finally {
        setIsInitializing(false);
      }
    };
    checkActiveSession();
  }, []);

  const handleLogin = useCallback(async () => {
    setError("");

    if (!username.trim() || !password.trim()) {
      setError("Ingresá tu usuario y contraseña");
      return;
    }

    setIsLoading(true);
    try {
      const result = await signIn(username.trim(), password);

      if (result.type === "NEW_PASSWORD_REQUIRED") {
        setPendingUser(result.cognitoUser!);
        setPendingAttributes(result.userAttributes || {});
        setStep("NEW_PASSWORD");
      } else {
        const token = await getCurrentSessionToken();
        if (!token) {
          throw new Error("No se pudo obtener el token de sesión.");
        }
        const profile = await fetchUserProfile(token);
        if (profile.roles.includes("ADMIN")) {
          setUserProfile(profile);
          setStep("AUTHENTICATED");
        } else {
          setError("Acceso denegado. No tenés permisos de administrador.");
        }
      }
    } catch (err: any) {
      const message =
        err?.code === "NotAuthorizedException"
          ? "Usuario o contraseña incorrectos"
          : err?.code === "UserNotFoundException"
          ? "El usuario no existe"
          : err?.code === "UserNotConfirmedException"
          ? "Tu cuenta aún no fue confirmada"
          : err?.message || "Error al iniciar sesión";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [username, password]);

  const handleNewPassword = useCallback(async () => {
    setError("");

    if (!newPassword.trim()) {
      setError("Ingresá tu nueva contraseña");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    if (newPassword.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return;
    }

    if (!pendingUser) {
      setError("Error inesperado. Intentá iniciar sesión nuevamente.");
      setStep("LOGIN");
      return;
    }

    setIsLoading(true);
    try {
      await completeNewPassword(pendingUser, newPassword, pendingAttributes);
      
      const token = await getCurrentSessionToken();
      if (!token) {
        throw new Error("No se pudo obtener el token de sesión.");
      }
      const profile = await fetchUserProfile(token);
      if (profile.roles.includes("ADMIN")) {
        setUserProfile(profile);
        setStep("AUTHENTICATED");
      } else {
        setError("Acceso denegado. No tenés permisos de administrador.");
      }
    } catch (err: any) {
      const message =
        err?.code === "InvalidPasswordException"
          ? "La contraseña no cumple los requisitos de seguridad"
          : err?.message || "Error al cambiar la contraseña";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [newPassword, confirmPassword, pendingUser, pendingAttributes]);

  const handleLogout = useCallback(() => {
    try {
      signOutCurrentUser();
    } catch (e) {
      console.error("SignOut error", e);
    }
    setStep("LOGIN");
    setUsername("");
    setPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setPendingUser(null);
    setPendingAttributes({});
    setUserProfile(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const token = await getCurrentSessionToken();
      if (token) {
        const profile = await fetchUserProfile(token);
        setUserProfile(profile);
      }
    } catch (e) {
      console.error("Error refreshing user profile:", e);
    }
  }, []);

  return {
    username,
    password,
    newPassword,
    confirmPassword,
    error,
    isLoading,
    step,
    userProfile,
    setUsername,
    setPassword,
    setNewPassword,
    setConfirmPassword,
    handleLogin,
    handleNewPassword,
    handleLogout,
    refreshProfile,
    isInitializing,
  };
}
